import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useCollectionBinding, usePropertyField } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function Navigation() {
  const scheduleTourLink = useSlotGroup('navigation.schedule_tour_link', scheduleTourLinkDef);

  const virtualTours = useCollectionBinding<VirtualTour>('navigation.virtual_tours', {
    collection: 'virtual_tours',
  });

  const hasVirtualTours = virtualTours.some((t) => t.virtual_tour_url);

  // Show Interactive Map link only when the property has it configured
  const interactiveMapBlurb = usePropertyField('property_interactivemap_blurb');
  const hasInteractiveMap = Boolean(interactiveMapBlurb);

  return (
    <nav className="bg-[var(--vibe-color-surface)] border-b border-[var(--vibe-color-border)] sticky top-20 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 overflow-x-auto">
          <div className="flex items-center gap-6">
            <a href="/" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
              Home
            </a>
            {hasInteractiveMap && (
              <a href="/interactive-map" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
                Interactive Map
              </a>
            )}
            {hasVirtualTours && (
              <a href="/virtual-tour" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
                Virtual Tour
              </a>
            )}
            <a href="/amenities" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
              Amenities
            </a>
            <a href="/gallery" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
              Gallery
            </a>
            <a href="/neighborhood" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
              Neighborhood
            </a>
            <a href="/contact-us" className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap">
              Contact Us
            </a>
          </div>

          <EditableSlotGroup groupPath="navigation.schedule_tour_link" definition={scheduleTourLinkDef}>
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              className="hidden md:inline-flex items-center px-4 py-1.5 text-xs font-semibold tracking-wider uppercase bg-[var(--vibe-color-primary)] text-white hover:bg-[var(--vibe-color-secondary)] transition-colors whitespace-nowrap"
            >
              {scheduleTourLink.label || 'Schedule A Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </nav>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Navigation',
  displayName: 'Navigation',
  slotGroups: {
    'navigation.schedule_tour_link': scheduleTourLinkDef,
  },
  collectionBindings: {
    'navigation.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Navigation as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Navigation;
